/** Admin panel — full ops console.
 *  Tabs: Overview · Stations · Claims · Users · Payments · Newsletter · NCA · Crawler */
import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { NavBar } from "@/components/NavBar";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const C = T.dark;

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "stations", label: "Stations" },
  { id: "claims", label: "Claims" },
  { id: "users", label: "Users" },
  { id: "payments", label: "Payments" },
  { id: "newsletter", label: "Newsletter" },
  { id: "nca", label: "NCA Registry" },
  { id: "crawler", label: "Crawler" },
  { id: "panel", label: "Panel" },
  { id: "fingerprint", label: "Fingerprint" },
  { id: "discovery", label: "Discovery" },
  { id: "blog", label: "Blog" },
  { id: "audit", label: "Audit Trail" },
  { id: "streams", label: "Streams" },
];

const tabBtn = (active) => ({
  padding: "8px 14px", borderRadius: 8,
  border: `1px solid ${active ? C.cyan + "44" : C.border}`,
  background: active ? C.cyan + "12" : "transparent",
  color: active ? C.cyan : C.muted, cursor: "pointer", fontSize: 13,
  fontWeight: active ? 700 : 500,
});
const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 };
const th = { textAlign: "left", padding: "10px 12px", color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${C.border}` };
const td = { padding: "11px 12px", color: C.text, fontSize: 13, borderBottom: `1px solid ${C.border}` };
const btn = (variant = "primary") => ({
  padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
  background: variant === "danger" ? "#EF4444" : variant === "ghost" ? "transparent" : T.greenAccent,
  color: variant === "ghost" ? C.muted : "#fff",
  border: variant === "ghost" ? `1px solid ${C.border}` : "none",
});

export default function AdminPanel() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [stations, setStations] = useState([]);
  const [claims, setClaims] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [nca, setNca] = useState([]);
  const [crawler, setCrawler] = useState(null);
  const [panelStats, setPanelStats] = useState(null);
  const [panelResponses, setPanelResponses] = useState([]);
  const [streamsQueue, setStreamsQueue] = useState([]);
  const [streamsHosting, setStreamsHosting] = useState(null);
  const [streamsConfigs, setStreamsConfigs] = useState([]);
  const [streamsClicks, setStreamsClicks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditVerify, setAuditVerify] = useState(null);
  // Fingerprint tab state
  const [fpStatus, setFpStatus] = useState(null);
  const [fpSettings, setFpSettings] = useState(null);
  const [fpRefs, setFpRefs] = useState([]);
  const [fpUploadCampaign, setFpUploadCampaign] = useState("");
  const [fpUploadTitle, setFpUploadTitle] = useState("");
  const [fpUploadFile, setFpUploadFile] = useState(null);
  const [fpUploading, setFpUploading] = useState(false);
  const [fpCalibFile, setFpCalibFile] = useState(null);
  const [fpCalibExpected, setFpCalibExpected] = useState("");
  const [fpCalibResult, setFpCalibResult] = useState(null);
  const [fpCalibRunning, setFpCalibRunning] = useState(false);
  // Discovery tab state
  const [discStatus, setDiscStatus] = useState(null);
  const [discCandidates, setDiscCandidates] = useState([]);
  const [discFilter, setDiscFilter] = useState({ status: "pending", country: "", source: "" });
  const [discBusy, setDiscBusy] = useState(false);
  const [discRunning, setDiscRunning] = useState(false);
  // Blog editorial-hold state (F-023)
  const [blogDrafts, setBlogDrafts] = useState([]);
  const [blogBusy, setBlogBusy] = useState(false);
  const [playbackTel, setPlaybackTel] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [toast, setToast] = useState("");
  // Modals
  const [ncaModal, setNcaModal] = useState(null); // {licence_number, station_name, licensee, region, frequency}
  const [bulkNcaModal, setBulkNcaModal] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [planModal, setPlanModal] = useState(null); // {user, plan}
  const [rejectModal, setRejectModal] = useState(null); // {claim, reason}
  const [confirmModal, setConfirmModal] = useState(null); // {title, body, onConfirm, variant}

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };
  const refreshOverview = async () => { const r = await api.get("/admin/overview"); setOverview(r.data); };

  useEffect(() => {
    (async () => {
      try {
        if (tab === "overview") { await refreshOverview(); }
        if (tab === "stations") { const r = await api.get("/admin/stations?limit=500"); setStations(r.data.stations || []); }
        if (tab === "claims") {
          // Use the hardened-flow review queue (code-verified, awaiting admin).
          // Falls back to legacy pending-claims if review-queue is empty so
          // pre-redesign claims still surface.
          const r = await api.get("/admin/claims/review-queue?limit=200");
          let queue = r.data.queue || [];
          if (queue.length === 0) {
            const legacy = await api.get("/admin/stations/pending-claims");
            queue = legacy.data.claims || [];
          }
          setClaims(queue);
        }
        if (tab === "users") { const r = await api.get(`/admin/users?limit=500${searchQ ? `&q=${encodeURIComponent(searchQ)}` : ""}`); setUsers(r.data.users || []); }
        if (tab === "payments") { const r = await api.get("/admin/payments?limit=200"); setPayments(r.data.payments || []); }
        if (tab === "newsletter") { const r = await api.get("/admin/newsletter?limit=500"); setNewsletter(r.data.subscribers || []); }
        if (tab === "nca") { const r = await api.get(`/admin/nca-registry${searchQ ? `?q=${encodeURIComponent(searchQ)}` : ""}`); setNca(r.data.entries || []); }
        if (tab === "audit") {
          const r = await api.get("/admin/audit?limit=200");
          setAudit(r.data.events || []);
        }
        if (tab === "crawler") {
          const [c, fp, pt] = await Promise.all([
            api.get("/admin/status"),
            api.get("/admin/fp/status").catch(() => ({ data: null })),
            api.get("/admin/playback-telemetry?days=7").catch(() => ({ data: null })),
          ]);
          setCrawler(c.data);
          if (fp.data) setFpStatus(fp.data);
          if (pt.data) setPlaybackTel(pt.data);
        }
        if (tab === "fingerprint") {
          const [s, cfg, refs] = await Promise.all([
            api.get("/admin/fp/status"),
            api.get("/admin/fp/settings"),
            api.get("/admin/fp/refs?limit=200"),
          ]);
          setFpStatus(s.data);
          setFpSettings(cfg.data);
          setFpRefs(refs.data.refs || []);
        }
        if (tab === "discovery") {
          const params = new URLSearchParams();
          if (discFilter.status) params.set("status", discFilter.status);
          if (discFilter.country) params.set("country", discFilter.country);
          if (discFilter.source) params.set("source", discFilter.source);
          const [st, cs] = await Promise.all([
            api.get("/admin/discovery/status"),
            api.get(`/admin/discovery/candidates?${params.toString()}`),
          ]);
          setDiscStatus(st.data);
          setDiscCandidates(cs.data.candidates || []);
        }
        if (tab === "blog") {
          const r = await api.get("/admin/blog/drafts");
          setBlogDrafts(r.data.drafts || []);
        }
        if (tab === "panel") {
          const [stats, resp] = await Promise.all([
            api.get("/ussd/panel/stats"),
            api.get("/ussd/panel/responses?limit=50"),
          ]);
          setPanelStats(stats.data);
          setPanelResponses(resp.data.responses || resp.data.panelists || []);
        }
        if (tab === "streams") {
          const [q, configs, clicks] = await Promise.all([
            api.get("/admin/streams/queue"),
            api.get("/admin/streams/configs"),
            api.get("/admin/telemetry/clicks?days=30").catch(() => ({ data: { rows: [] } })),
          ]);
          setStreamsQueue(q.data.queue || []);
          setStreamsHosting(q.data.hosting || null);
          setStreamsConfigs(configs.data.configs || []);
          setStreamsClicks(clicks.data.rows || []);
        }
      } catch (e) { showToast(e.response?.data?.detail || "Load failed"); }
    })();
  }, [tab, searchQ, discFilter]);

  const triggerCrawl = async () => {
    try { await api.post("/admin/crawl"); showToast("Crawl cycle triggered"); }
    catch (e) { showToast(e.response?.data?.detail || "Failed"); }
  };
  const approveClaim = async (claim) => {
    try { await api.post(`/admin/claims/${claim.id}/approve`); showToast("Claim approved");
      setClaims((cs) => cs.filter((c) => c.id !== claim.id)); }
    catch (e) { showToast(e.response?.data?.detail || "Failed"); }
  };
  const rejectClaim = (claim) => setRejectModal({ claim, reason: "Not authorised" });

  const downloadClaimDoc = async (claimId) => {
    // Stream the uploaded NCA licence PDF/image — opens in a new tab so the
    // admin can flip between this and the registry row without losing the queue.
    try {
      const r = await api.get(`/admin/claims/${claimId}/licence-doc`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      window.open(url, "_blank");
      // Free the blob URL after a brief delay — long enough for the browser to load it.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      showToast(e.response?.data?.detail || "Could not load licence document");
    }
  };

  const updateNcaContactEmail = async (entry, newEmail) => {
    const cleaned = (newEmail || "").trim();
    if (cleaned === (entry.contact_email || "")) return;  // no change
    try {
      const { data } = await api.patch(`/admin/nca-registry/${entry.id}`, { contact_email: cleaned || null });
      setNca((rows) => rows.map((r) => r.id === entry.id ? data : r));
      showToast(cleaned ? "Contact email saved" : "Contact email cleared");
    } catch (e) {
      const detail = e.response?.data?.detail;
      showToast(typeof detail === "string" ? detail : (detail?.[0]?.msg || "Save failed"));
    }
  };
  const confirmRejectClaim = async () => {
    const { claim, reason } = rejectModal;
    try { await api.post(`/admin/claims/${claim.id}/reject`, { reason }); showToast("Claim rejected");
      setClaims((cs) => cs.filter((c) => c.id !== claim.id)); setRejectModal(null); }
    catch (e) { showToast(e.response?.data?.detail || "Failed"); }
  };
  const toggleSuspend = (u) => {
    const suspend = u.is_active;
    setConfirmModal({
      title: `${suspend ? "Suspend" : "Reactivate"} ${u.email}?`,
      body: suspend
        ? "All active access tokens for this user will be revoked immediately. They will be signed out on every device."
        : "The user will be able to sign in again on their next login attempt.",
      variant: suspend ? "danger" : "primary",
      ctaLabel: suspend ? "Suspend user" : "Reactivate user",
      onConfirm: async () => {
        try {
          await api.post(`/admin/users/${u.id}/suspend`, { suspend });
          showToast(suspend ? "User suspended" : "User reactivated");
          setUsers((us) => us.map((x) => x.id === u.id ? { ...x, is_active: !suspend } : x));
          setConfirmModal(null);
        } catch (e) { showToast(e.response?.data?.detail || "Failed"); }
      },
    });
  };
  const setUserPlan = (u) => setPlanModal({ user: u, plan: u.plan || "free" });
  const confirmSetPlan = async () => {
    const { user: u, plan } = planModal;
    try { await api.post(`/admin/users/${u.id}/plan`, { plan }); showToast(`Plan set to ${plan}`);
      setUsers((us) => us.map((x) => x.id === u.id ? { ...x, plan } : x)); setPlanModal(null); }
    catch (e) { showToast(e.response?.data?.detail || "Failed"); }
  };
  const deleteUser = (u) => {
    setConfirmModal({
      title: `Delete ${u.email}?`,
      body: (
        "This will SOFT-DELETE the account. PII is redacted, the password is wiped, " +
        "active tokens revoked, claimed stations released and owned campaigns archived. " +
        "The audit trail keeps a hash-chained record. The user cannot sign in again."
      ),
      variant: "danger",
      ctaLabel: "Delete account",
      onConfirm: async () => {
        try {
          await api.delete(`/admin/users/${u.id}`, { data: { reason: "Admin removal" } });
          showToast("User deleted");
          setUsers((us) => us.filter((x) => x.id !== u.id));
          setConfirmModal(null);
        } catch (e) { showToast(e.response?.data?.detail || "Delete failed"); }
      },
    });
  };
  const verifyAuditChain = async () => {
    setAuditVerify({ loading: true });
    try {
      const r = await api.get("/admin/audit/verify");
      setAuditVerify(r.data);
      showToast(r.data.ok ? `✓ Chain intact (${r.data.rows_checked} rows)` : `⚠ Chain broken at ${r.data.broken_at}`);
    } catch (e) {
      setAuditVerify({ ok: false, error: e.message });
      showToast("Verify failed");
    }
  };
  const openAddNca = () => setNcaModal({ licence_number: "", station_name: "", licensee: "", region: "", frequency: "" });
  const submitAddNca = async () => {
    const m = ncaModal;
    if (!m.licence_number || !m.station_name) return showToast("Licence # and station name required");
    try { await api.post("/admin/nca-registry", m); showToast("Added");
      const r = await api.get("/admin/nca-registry"); setNca(r.data.entries || []); setNcaModal(null); }
    catch (e) { showToast(e.response?.data?.detail || "Failed"); }
  };
  const submitBulkNca = async () => {
    // Parse CSV: licence_number,station_name,licensee,frequency,region (header optional)
    const lines = bulkCsv.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return showToast("Paste CSV rows");
    const startIdx = /^licence/i.test(lines[0]) ? 1 : 0;
    const entries = [];
    for (let i = startIdx; i < lines.length; i++) {
      const cols = lines[i].split(",").map((c) => c.trim());
      if (cols.length < 2 || !cols[0]) continue;
      entries.push({
        licence_number: cols[0],
        station_name: cols[1],
        licensee: cols[2] || "",
        frequency: cols[3] || "",
        region: cols[4] || "",
      });
    }
    if (entries.length === 0) return showToast("Could not parse any rows");
    try {
      const r = await api.post("/admin/nca-registry/bulk", { entries });
      setBulkResult(r.data);
      const list = await api.get("/admin/nca-registry");
      setNca(list.data.entries || []);
    } catch (e) { showToast(e.response?.data?.detail || "Failed"); }
  };
  const deleteNca = (entry) => {
    setConfirmModal({
      title: `Delete ${entry.licence_number}?`,
      body: `${entry.station_name} will be removed from the NCA registry. This is a soft delete on the application layer — the seed file will re-insert it on next backend restart unless you also remove it from the seed.`,
      variant: "danger",
      ctaLabel: "Delete entry",
      onConfirm: async () => {
        try {
          await api.delete(`/admin/nca-registry/${entry.id}`);
          showToast("Deleted");
          setNca((xs) => xs.filter((x) => x.id !== entry.id));
          setConfirmModal(null);
        } catch (e) { showToast(e.response?.data?.detail || "Failed"); }
      },
    });
  };

  return (
    <div data-testid="admin-panel" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`body { margin:0; background:${C.bg} } @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap')`}</style>
      <NavBar dark />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px 24px" }}>
        <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 28, fontWeight: 800, margin: "0 0 6px" }}>
          Admin Console
        </h1>
        <p style={{ color: C.muted, margin: "0 0 22px", fontSize: 13 }}>
          Full operations panel for station catalogue, claims moderation, billing, and the NCA registry.
        </p>

        <div style={{ display: "flex", gap: 6, marginBottom: 22, flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => { setTab(t.id); setSearchQ(""); }}
              style={tabBtn(tab === t.id)}>{t.label}</button>
          ))}
        </div>

        {toast && <div data-testid="admin-toast" style={{ position: "fixed", top: 90, right: 24, padding: "10px 18px",
          background: T.greenAccent, color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 100 }}>
          {toast}</div>}

        {/* OVERVIEW */}
        {tab === "overview" && overview && (
          <div data-testid="admin-overview" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
            {[
              ["Users (active)", `${overview.users_active}/${overview.users_total}`],
              ["Stations (verified)", `${overview.stations_verified}/${overview.stations_total}`],
              ["Streaming stations", overview.stations_streaming],
              ["Pending claims", overview.pending_claims],
              ["Verified claims", overview.verified_claims],
              ["Campaigns", overview.campaigns_total],
              ["Certificates issued", overview.certificates_total],
              ["Successful payments", overview.payments_success],
              ["Newsletter subscribers", overview.newsletter_subscribers],
              ["NCA registry entries", overview.nca_registry_entries],
            ].map(([k, v]) => (
              <div key={k} style={card}>
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>{k}</div>
                <div style={{ color: C.text, fontSize: 28, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif" }}>{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* STATIONS */}
        {tab === "stations" && (
          <div style={card}>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>{stations.length} stations</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Name</th><th style={th}>Country</th><th style={th}>Freq</th><th style={th}>Streaming</th><th style={th}>Stream health</th><th style={th}>Verified</th></tr></thead>
                <tbody>{stations.map((s) => (
                  <tr key={s.id} data-testid={`admin-station-${s.id}`}>
                    <td style={td}>{s.name}</td>
                    <td style={td}>{s.country}</td>
                    <td style={td}>{s.frequency || "—"}</td>
                    <td style={td}>{s.is_streaming ? "✓" : "—"}</td>
                    <td style={td} data-testid={`stream-health-${s.id}`}>
                      {s.stream_health?.grade ? (
                        <span style={{ fontWeight: 800,
                          color: s.stream_health.grade === "A" ? T.greenAccent : s.stream_health.grade === "B" ? "#F59E0B" : "#EF4444" }}
                          title={`7-day drop rate ${Math.round((s.stream_health.drop_rate || 0) * 100)}% · ${s.stream_health.plays_7d || 0} plays · median drop at ${s.stream_health.median_seconds_to_drop ?? "—"}s`}>
                          {s.stream_health.grade}
                          <span style={{ color: C.muted, fontWeight: 400, fontSize: 10, marginLeft: 4 }}>
                            {Math.round((s.stream_health.drop_rate || 0) * 100)}%
                          </span>
                        </span>
                      ) : <span style={{ color: C.muted }}>—</span>}
                    </td>
                    <td style={td}>{s.is_verified ? <span style={{ color: T.greenAccent }}>✓ Verified</span> : <span style={{ color: C.muted }}>pending</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* CLAIMS */}
        {tab === "claims" && (
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ color: C.muted, fontSize: 12 }}>
                {claims.length} in review queue ·
                <span style={{ color: T.greenAccent, marginLeft: 8 }}>NCA-registered email verified</span> ·
                <span style={{ color: "#EF4444", marginLeft: 8 }}>SLA 48 h</span>
              </div>
            </div>
            {claims.length === 0 && <div style={{ color: C.muted, padding: "20px 0", fontSize: 13 }}>No claims in review. The hardened flow funnels all approvals here after the 6-digit NCA-email code matches.</div>}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>Station</th>
                  <th style={th}>Claimant</th>
                  <th style={th}>NCA-registered email</th>
                  <th style={th}>NCA #</th>
                  <th style={th}>Licence PDF</th>
                  <th style={th}>Hours in queue</th>
                  <th style={th}>Flags</th>
                  <th style={th}>Actions</th>
                </tr></thead>
                <tbody>{claims.map((c) => (
                  <tr key={c.id} data-testid={`admin-claim-${c.id}`}
                      style={c.sla_breached ? { background: "#FEF2F222" } : undefined}>
                    <td style={td}><b>{c.station_name || c.station_id?.slice(0, 8)}</b></td>
                    <td style={td}>
                      <div style={{ fontSize: 12 }}>{c.claimant_email || c.contact_email || "—"}</div>
                      {c.claimant_full_name && <div style={{ color: C.muted, fontSize: 10 }}>{c.claimant_full_name}</div>}
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>
                      {c.nca_registered_email
                        ? <span style={{ color: T.greenAccent }}>✓ {c.nca_registered_email}</span>
                        : <span style={{ color: "#EF4444" }}>— missing —</span>}
                    </td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{c.nca_licence_number || "—"}</td>
                    <td style={td}>
                      {c.licence_doc_id ? (
                        <a data-testid={`claim-doc-${c.id}`} href="#"
                          onClick={(ev) => { ev.preventDefault(); downloadClaimDoc(c.id); }}
                          style={{ color: T.cyan, textDecoration: "underline", fontSize: 12, cursor: "pointer" }}>
                          📄 View
                        </a>
                      ) : <span style={{ color: C.muted, fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: c.sla_breached ? "#EF4444" : C.text }}>
                      {c.hours_in_queue ?? "—"} h{c.sla_breached ? " ⚠" : ""}
                    </td>
                    <td style={{ ...td, fontSize: 10 }}>
                      {c.station_name_mismatch && <span style={{ background: "#FEF3C7", color: "#92400E", padding: "2px 6px", borderRadius: 4, marginRight: 4 }}>name mismatch</span>}
                    </td>
                    <td style={td}>
                      <button data-testid={`approve-${c.id}`} onClick={() => approveClaim(c)} style={{ ...btn(), marginRight: 6 }}>Approve</button>
                      <button data-testid={`reject-${c.id}`} onClick={() => rejectClaim(c)} style={btn("danger")}>Reject</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* USERS */}
        {tab === "users" && (
          <div style={card}>
            <input data-testid="admin-users-search" placeholder="Search by email, name, organisation…"
              value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`,
                color: C.text, borderRadius: 8, marginBottom: 14, fontFamily: "inherit", fontSize: 13 }} />
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>{users.length} users</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Email</th><th style={th}>Name</th><th style={th}>Role</th><th style={th}>Plan</th><th style={th}>Status</th><th style={th}>Actions</th></tr></thead>
                <tbody>{users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                    <td style={td}>{u.email}</td>
                    <td style={td}>{u.full_name || "—"}</td>
                    <td style={td}>{u.role}</td>
                    <td style={td}>{u.plan || "free"}</td>
                    <td style={td}>{u.is_active === false ? <span style={{ color: "#EF4444" }}>suspended</span> : <span style={{ color: T.greenAccent }}>active</span>}</td>
                    <td style={td}>
                      <button data-testid={`suspend-${u.id}`} onClick={() => toggleSuspend(u)} style={{ ...btn(u.is_active ? "danger" : "primary"), marginRight: 6 }}>
                        {u.is_active ? "Suspend" : "Reactivate"}
                      </button>
                      <button data-testid={`plan-${u.id}`} onClick={() => setUserPlan(u)} style={{ ...btn("ghost"), marginRight: 6 }}>Set plan</button>
                      <button data-testid={`delete-${u.id}`} onClick={() => deleteUser(u)} style={btn("danger")}>Delete</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* PAYMENTS */}
        {tab === "payments" && (
          <div style={card}>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>{payments.length} payments</div>
            {payments.length === 0 && <div style={{ color: C.muted, padding: "20px 0", fontSize: 13 }}>No payments yet.</div>}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Ref</th><th style={th}>User</th><th style={th}>Plan</th><th style={th}>Amount</th><th style={th}>Status</th><th style={th}>Created</th></tr></thead>
                <tbody>{payments.map((p) => (
                  <tr key={p.id}>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{p.reference}</td>
                    <td style={td}>{p.user_email || "—"}</td>
                    <td style={td}>{p.plan}</td>
                    <td style={td}>GH₵ {(p.amount_kobo / 100).toLocaleString()}</td>
                    <td style={td}>{p.status === "success" ? <span style={{ color: T.greenAccent }}>✓ success</span> : <span style={{ color: "#F59E0B" }}>{p.status}</span>}</td>
                    <td style={td}>{(p.created_at || "").slice(0, 16)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* NEWSLETTER */}
        {tab === "newsletter" && (
          <div style={card}>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>{newsletter.length} subscribers</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Email</th><th style={th}>Country</th><th style={th}>Subscribed</th></tr></thead>
                <tbody>{newsletter.map((n, i) => (
                  <tr key={i}>
                    <td style={td}>{n.email}</td>
                    <td style={td}>{n.country}</td>
                    <td style={td}>{(n.subscribed_at || "").slice(0, 16)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* NCA */}
        {tab === "nca" && (
          <div style={card}>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input data-testid="admin-nca-search" placeholder="Search by licence# / station / licensee…"
                value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                style={{ flex: 1, padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`,
                  color: C.text, borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
              <button data-testid="admin-nca-add" onClick={openAddNca} style={btn()}>+ Add</button>
              <button data-testid="admin-nca-bulk" onClick={() => { setBulkNcaModal(true); setBulkCsv(""); setBulkResult(null); }} style={btn("ghost")}>Bulk import</button>
            </div>
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>{nca.length} entries</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Licence #</th><th style={th}>Station</th><th style={th}>Licensee</th><th style={th}>Contact email</th><th style={th}>Region</th><th style={th}>Status</th><th style={th}>Actions</th></tr></thead>
                <tbody>{nca.map((e) => (
                  <tr key={e.id} data-testid={`admin-nca-${e.id}`}>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{e.licence_number}</td>
                    <td style={td}>{e.station_name}</td>
                    <td style={td}>{e.licensee || "—"}</td>
                    <td style={td}>
                      <input data-testid={`nca-email-${e.id}`} defaultValue={e.contact_email || ""}
                        placeholder="claims@…"
                        onBlur={(ev) => updateNcaContactEmail(e, ev.target.value)}
                        style={{ width: 180, padding: "4px 8px", background: C.bg,
                                 border: `1px solid ${e.contact_email ? T.greenAccent + "55" : C.border}`,
                                 color: C.text, borderRadius: 6, fontSize: 11, fontFamily: "inherit" }} />
                      {e.contact_email && <span style={{ color: T.greenAccent, marginLeft: 4 }}>✓</span>}
                    </td>
                    <td style={td}>{e.region || "—"}</td>
                    <td style={td}>{e.status}</td>
                    <td style={td}>
                      <button data-testid={`nca-delete-${e.id}`} onClick={() => deleteNca(e)} style={btn("danger")}>Delete</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* CRAWLER */}
        {tab === "crawler" && crawler && (
          <div data-testid="admin-crawler" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={card}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Status</div>
              <div style={{ color: C.text, fontSize: 14, lineHeight: 2 }}>
                Cached stations: <b>{crawler.cached_stations}</b><br />
                Cycle interval: <b>{crawler.crawler_interval}s</b>
              </div>
              <button data-testid="admin-trigger-crawl" onClick={triggerCrawl} style={{ ...btn(), marginTop: 16 }}>Trigger crawl now</button>
            </div>
            <div style={card}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Recent crawls</div>
              {(crawler.recent_crawls || []).slice(0, 5).map((j, i) => (
                <div key={i} style={{ color: C.text, fontSize: 12, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                  {(j.started_at || "").slice(0, 19)} — {j.stations_crawled || j.stations_processed || 0} stations
                </div>
              ))}
            </div>
            {/* Playback reliability — Part 0 telemetry aggregation */}
            <div data-testid="admin-playback-telemetry" style={{ ...card, gridColumn: "1 / span 2" }}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
                Playback reliability · last {playbackTel?.days ?? 7} days · {playbackTel?.total_events ?? 0} events
              </div>
              {!playbackTel?.stations?.length ? (
                <div style={{ color: C.muted, fontSize: 12 }}>
                  No playback telemetry yet. Events arrive as listeners use the /listen player —
                  drops clustering at ~55–65s on <b>proxied</b> sources indicate the platform timeout (see RELAY.md);
                  drops at odd times on <b>direct</b> URLs are the station's own stream.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={th}>Station</th><th style={th}>Plays</th><th style={th}>Drops</th>
                      <th style={th}>Median s→drop</th><th style={th}>Drop rate</th>
                      <th style={th}>Recovery</th><th style={th}>Sources</th><th style={th}>Drop types</th>
                    </tr></thead>
                    <tbody>{playbackTel.stations.map((r) => (
                      <tr key={r.station_id} data-testid={`playback-tel-${r.station_id}`}>
                        <td style={td}>{r.name}{r.stream_health?.grade && (
                          <span style={{ marginLeft: 6, fontWeight: 800, fontSize: 10,
                            color: r.stream_health.grade === "A" ? T.greenAccent : r.stream_health.grade === "B" ? "#F59E0B" : "#EF4444" }}>
                            {r.stream_health.grade}</span>)}</td>
                        <td style={td}>{r.plays}</td>
                        <td style={{ ...td, color: r.drops ? "#EF4444" : C.muted }}>{r.drops}</td>
                        <td style={{ ...td,
                          color: r.median_seconds_to_drop != null && r.median_seconds_to_drop >= 55 && r.median_seconds_to_drop <= 65 ? "#F59E0B" : C.text }}>
                          {r.median_seconds_to_drop ?? "—"}
                          {r.median_seconds_to_drop != null && r.median_seconds_to_drop >= 55 && r.median_seconds_to_drop <= 65 && " ⚠ ~60s"}
                        </td>
                        <td style={td}>{r.drop_rate != null ? `${Math.round(r.drop_rate * 100)}%` : "—"}</td>
                        <td style={td}>{r.recovery_rate != null ? `${Math.round(r.recovery_rate * 100)}%` : "—"}</td>
                        <td style={{ ...td, fontSize: 10 }}>{Object.entries(r.source_kind_breakdown || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}</td>
                        <td style={{ ...td, fontSize: 10 }}>{Object.entries(r.event_type_breakdown || {}).filter(([k]) => ["error", "ended_unexpected", "stalled"].includes(k)).map(([k, v]) => `${k}:${v}`).join(" · ") || "—"}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
            {/* Fingerprint pipeline health — Part 6 */}
            {fpStatus && (
              <div data-testid="admin-crawler-fp-health" style={{ ...card, gridColumn: "1 / span 2" }}>
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Fingerprint pipeline</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
                  {[
                    { label: "Master gate", value: fpStatus.fingerprinting_enabled ? "ON" : "OFF", accent: fpStatus.fingerprinting_enabled ? "#10B981" : "#EF4444" },
                    { label: "Segmenter", value: fpStatus.segmenter_enabled ? "ON" : "OFF", accent: fpStatus.segmenter_enabled ? "#10B981" : C.muted },
                    { label: "Matcher", value: fpStatus.matcher_enabled ? "ON" : "OFF", accent: fpStatus.matcher_enabled ? "#10B981" : C.muted },
                    { label: "ffmpeg", value: fpStatus.ffmpeg_present ? "READY" : "MISSING", accent: fpStatus.ffmpeg_present ? "#10B981" : "#EF4444" },
                    { label: "FP stations", value: fpStatus.fp_enabled_stations, accent: C.cyan },
                    { label: "Refs", value: fpStatus.refs_total, accent: C.text },
                    { label: "Hashes", value: fpStatus.hashes_total.toLocaleString(), accent: C.text },
                    { label: "Postings loaded", value: fpStatus.postings_loaded.toLocaleString(), accent: C.text },
                    { label: "Captures", value: fpStatus.active_captures, accent: C.text },
                    { label: "Segments on disk", value: fpStatus.segment_files_on_disk, accent: C.text },
                    { label: "min-hits", value: fpStatus.match_min_hits, accent: C.muted },
                    { label: "cert grade", value: fpStatus.cert_grade, accent: C.muted },
                  ].map((s, i) => (
                    <div key={i} style={{ padding: 10, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{s.label}</div>
                      <div style={{ color: s.accent, fontFamily: "'Space Grotesk',sans-serif", fontSize: 18, fontWeight: 800 }}>{s.value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ color: C.muted, fontSize: 11, marginTop: 12 }}>
                  For calibration & reference management, use the <button onClick={() => setTab("fingerprint")} style={{ background: "transparent", border: "none", color: C.cyan, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Fingerprint</button> tab.
                </div>
              </div>
            )}
          </div>
        )}

        {/* FINGERPRINT — Part 5 Calibration harness */}
        {tab === "fingerprint" && fpStatus && fpSettings && (
          <div data-testid="admin-fingerprint" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Master status banner */}
            <div style={{ ...card, background: fpSettings.gate_open_for_certs ? "#064E3B22" : "#78350F22", borderColor: fpSettings.gate_open_for_certs ? "#10B98155" : "#F59E0B55" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ color: fpSettings.gate_open_for_certs ? "#34D399" : "#FBBF24", fontSize: 14, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                    {fpSettings.gate_open_for_certs ? "CERT MINTING · LIVE" : "CERT MINTING · GATED"}
                  </div>
                  <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
                    env master: <b style={{ color: fpSettings.env_master.FINGERPRINTING_ENABLED ? "#34D399" : "#EF4444" }}>{fpSettings.env_master.FINGERPRINTING_ENABLED ? "ON" : "OFF"}</b> · dynamic flag: <b style={{ color: fpSettings.effective.FINGERPRINTING_ENABLED ? "#34D399" : "#EF4444" }}>{fpSettings.effective.FINGERPRINTING_ENABLED ? "ON" : "OFF"}</b>
                  </div>
                </div>
                <button
                  data-testid="fp-toggle-master"
                  onClick={async () => {
                    try {
                      const next = !fpSettings.effective.FINGERPRINTING_ENABLED;
                      const r = await api.post("/admin/fp/settings", { FINGERPRINTING_ENABLED: next });
                      setFpSettings((s) => ({ ...s, effective: r.data.effective, gate_open_for_certs: fpSettings.env_master.FINGERPRINTING_ENABLED && r.data.effective.FINGERPRINTING_ENABLED }));
                      showToast(next ? "Dynamic gate ON" : "Dynamic gate OFF");
                    } catch (e) { showToast(e.response?.data?.detail || "Failed"); }
                  }}
                  style={btn(fpSettings.effective.FINGERPRINTING_ENABLED ? "danger" : "primary")}>
                  {fpSettings.effective.FINGERPRINTING_ENABLED ? "Turn dynamic flag OFF" : "Turn dynamic flag ON"}
                </button>
              </div>
            </div>

            {/* Threshold controls */}
            <div style={card}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Thresholds</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Minimum aligned hits</label>
                  <input
                    data-testid="fp-input-min-hits"
                    type="number" min={2} max={200}
                    value={fpSettings.effective.FP_MATCH_MIN_HITS}
                    onChange={(e) => setFpSettings((s) => ({ ...s, effective: { ...s.effective, FP_MATCH_MIN_HITS: parseInt(e.target.value || 8, 10) } }))}
                    style={{ marginTop: 6, width: "100%", padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }} />
                  <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>Matches below this are silently ignored.</div>
                </div>
                <div>
                  <label style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Certificate grade (score 0-1)</label>
                  <input
                    data-testid="fp-input-cert-grade"
                    type="number" min={0} max={1} step={0.05}
                    value={fpSettings.effective.FP_CERT_GRADE}
                    onChange={(e) => setFpSettings((s) => ({ ...s, effective: { ...s.effective, FP_CERT_GRADE: parseFloat(e.target.value || 0.75) } }))}
                    style={{ marginTop: 6, width: "100%", padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }} />
                  <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>Only matches ≥ this promote to CERTIFIED.</div>
                </div>
              </div>
              <button
                data-testid="fp-save-thresholds"
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/fp/settings", {
                      FP_MATCH_MIN_HITS: fpSettings.effective.FP_MATCH_MIN_HITS,
                      FP_CERT_GRADE: fpSettings.effective.FP_CERT_GRADE,
                    });
                    setFpSettings((s) => ({ ...s, effective: r.data.effective }));
                    showToast("Thresholds saved");
                  } catch (e) { showToast(e.response?.data?.detail || "Save failed"); }
                }}
                style={{ ...btn(), marginTop: 12 }}>Save thresholds</button>
            </div>

            {/* Calibration */}
            <div style={card}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Calibration (dry-run match)</div>
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>
                Upload any audio (10-60 s recommended) and see the top matches against the current reference DB. No detections or certificates are written.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
                <div>
                  <label style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Query audio</label>
                  <input data-testid="fp-calib-file" type="file" accept="audio/*" onChange={(e) => setFpCalibFile(e.target.files?.[0] || null)}
                    style={{ marginTop: 6, width: "100%", padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>Expected ref (optional)</label>
                  <select data-testid="fp-calib-expected" value={fpCalibExpected} onChange={(e) => setFpCalibExpected(e.target.value)}
                    style={{ marginTop: 6, width: "100%", padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                    <option value="">— any —</option>
                    {fpRefs.map((r) => <option key={r.id} value={r.id}>{r.ad_title} · {r.id.slice(0,8)}</option>)}
                  </select>
                </div>
                <button
                  data-testid="fp-calib-run"
                  disabled={!fpCalibFile || fpCalibRunning}
                  onClick={async () => {
                    setFpCalibRunning(true);
                    try {
                      const fd = new FormData();
                      fd.append("audio", fpCalibFile);
                      if (fpCalibExpected) fd.append("expected_ref_id", fpCalibExpected);
                      fd.append("top_k", "5");
                      const r = await api.post("/admin/fp/calibrate", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setFpCalibResult(r.data);
                    } catch (e) { showToast(e.response?.data?.detail || "Calibration failed"); }
                    finally { setFpCalibRunning(false); }
                  }}
                  style={btn()}>{fpCalibRunning ? "Running…" : "Run match →"}</button>
              </div>
              {fpCalibResult && (
                <div data-testid="fp-calib-result" style={{ marginTop: 16, padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <div style={{ color: C.text, fontWeight: 700 }}>
                      Verdict: <span style={{ color: fpCalibResult.verdict === "MATCH" ? "#34D399" : fpCalibResult.verdict === "MISS" ? "#EF4444" : "#FBBF24" }}>{fpCalibResult.verdict || "n/a"}</span>
                    </div>
                    <div style={{ color: C.muted, fontSize: 12 }}>Query hashes: {fpCalibResult.query_hash_count}</div>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={th}>Rank</th><th style={th}>Ref</th><th style={th}>Hits</th><th style={th}>Score</th><th style={th}>Offset</th>
                    </tr></thead>
                    <tbody>{fpCalibResult.top.map((t, i) => (
                      <tr key={t.ref_id}>
                        <td style={td}>#{i + 1}</td>
                        <td style={td}>{t.ref_meta?.ad_title || "?"} <span style={{ color: C.muted }}>({t.ref_id.slice(0,8)})</span></td>
                        <td style={td}><b>{t.hits}</b></td>
                        <td style={td}>{t.score}</td>
                        <td style={td}>{t.offset_frames}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Reference DB */}
            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>Reference DB · {fpRefs.length} ads</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "end", marginBottom: 12, padding: 12, background: C.bg, borderRadius: 8 }}>
                <input data-testid="fp-ref-campaign" placeholder="Campaign ID"
                  value={fpUploadCampaign} onChange={(e) => setFpUploadCampaign(e.target.value)}
                  style={{ padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }} />
                <input data-testid="fp-ref-title" placeholder="Ad title"
                  value={fpUploadTitle} onChange={(e) => setFpUploadTitle(e.target.value)}
                  style={{ padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }} />
                <input data-testid="fp-ref-file" type="file" accept="audio/*"
                  onChange={(e) => setFpUploadFile(e.target.files?.[0] || null)}
                  style={{ padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }} />
                <button
                  data-testid="fp-ref-upload"
                  disabled={!fpUploadCampaign || !fpUploadTitle || !fpUploadFile || fpUploading}
                  onClick={async () => {
                    setFpUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append("campaign_id", fpUploadCampaign);
                      fd.append("ad_title", fpUploadTitle);
                      fd.append("audio", fpUploadFile);
                      const r = await api.post("/admin/fp/refs", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setFpRefs((rr) => [r.data.ref, ...rr]);
                      setFpUploadCampaign(""); setFpUploadTitle(""); setFpUploadFile(null);
                      showToast(`Ingested ${r.data.ref.hash_count} hashes`);
                    } catch (e) { showToast(e.response?.data?.detail || "Upload failed"); }
                    finally { setFpUploading(false); }
                  }}
                  style={btn()}>{fpUploading ? "Ingesting…" : "Upload"}</button>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>Ad title</th><th style={th}>Campaign</th><th style={th}>Duration</th><th style={th}>Hashes</th><th style={th}>SHA-256</th><th style={th}></th>
                </tr></thead>
                <tbody>
                  {fpRefs.map((r) => (
                    <tr key={r.id}>
                      <td style={td}>{r.ad_title}</td>
                      <td style={{ ...td, fontFamily: "monospace", fontSize: 11 }}>{r.campaign_id.slice(0, 8)}</td>
                      <td style={td}>{r.duration_seconds}s</td>
                      <td style={td}><b>{r.hash_count.toLocaleString()}</b></td>
                      <td style={{ ...td, fontFamily: "monospace", fontSize: 10 }}>{r.sha256.slice(0, 16)}…</td>
                      <td style={td}>
                        <button
                          data-testid={`fp-ref-delete-${r.id.slice(0,8)}`}
                          onClick={async () => {
                            try {
                              await api.delete(`/admin/fp/refs/${r.id}`);
                              setFpRefs((rr) => rr.filter((x) => x.id !== r.id));
                              showToast("Reference removed");
                            } catch (e) { showToast(e.response?.data?.detail || "Failed"); }
                          }}
                          style={btn("danger")}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {fpRefs.length === 0 && (
                    <tr><td colSpan={6} style={{ ...td, color: C.muted, textAlign: "center", padding: 24 }}>
                      No references yet. Upload an ad audio file above.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PANEL — USSD listener panel ops */}
        {tab === "panel" && (
          <div data-testid="admin-panel-tab" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
              {[
                { label: "Active panelists", value: (panelStats?.panelists?.total_active || 0).toLocaleString(), accent: C.cyan },
                { label: "Responses today",  value: (panelStats?.responses?.today || 0).toLocaleString(), accent: "#10B981" },
                { label: "Total responses",  value: (panelStats?.responses?.total || 0).toLocaleString(), accent: C.text },
                { label: "Paid out (GH₵)",   value: (panelStats?.responses?.total_paid_ghs || 0).toLocaleString(), accent: "#FBBF24" },
              ].map((c, i) => (
                <div key={i} data-testid={`panel-stat-${i}`} style={card}>
                  <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>{c.label}</div>
                  <div style={{ color: c.accent, fontFamily: "'Space Grotesk',sans-serif", fontSize: 28, fontWeight: 800 }}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={card}>
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Panelists by region</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={panelStats?.panelists?.by_region || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="region" tick={{ fill: C.muted, fontSize: 10 }} />
                    <YAxis tick={{ fill: C.muted, fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="count" fill={C.cyan} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={card}>
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Top 15 stations (7d)</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={(panelStats?.top_stations_7d || []).slice(0, 15)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis type="number" tick={{ fill: C.muted, fontSize: 10 }} />
                    <YAxis type="category" dataKey="station" width={110} tick={{ fill: C.muted, fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="responses" fill="#10B981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent responses table */}
            <div style={card}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>
                Recent survey responses ({panelResponses.length})
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: C.muted, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>
                      {["When", "Network", "Medium", "Station", "Duration", "Context", "Reward", "Conf."].map((h) => (
                        <th key={h} style={{ padding: "8px 10px", fontWeight: 700, letterSpacing: "0.04em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {panelResponses.map((r, i) => (
                      <tr key={r.id || i} data-testid={`panel-row-${i}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "8px 10px", color: C.muted, fontFamily: "monospace" }}>{(r.timestamp || "").replace("T", " ").slice(0, 16)}</td>
                        <td style={{ padding: "8px 10px", color: C.text }}>{r.network || "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.text }}>{r.medium || "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.text, fontWeight: 600 }}>{r.station_name || "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.muted }}>{r.duration || "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.muted }}>{r.context || "—"}</td>
                        <td style={{ padding: "8px 10px", color: "#FBBF24", fontWeight: 700 }}>GH₵ {(r.reward_ghs ?? 0).toFixed(2)}</td>
                        <td style={{ padding: "8px 10px" }}>
                          <span style={{ color: r.rewarded ? "#10B981" : "#EF4444", fontSize: 11, fontWeight: 700 }}>
                            {r.confidence || (r.rewarded ? "PANEL" : "PENDING")}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {panelResponses.length === 0 && (
                      <tr><td colSpan={8} style={{ padding: 24, color: C.muted, textAlign: "center" }}>No survey responses yet. They appear here once panelists complete a USSD survey.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* DISCOVERY — station discovery sweep queue */}
        {tab === "discovery" && discStatus && (
          <div data-testid="admin-discovery" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Status bar */}
            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Discovery sweep</div>
                  <div style={{ color: C.text, fontSize: 13 }}>
                    Sources: <b>{(discStatus.sources_configured || []).join(", ")}</b> · Countries: <b>{(discStatus.countries || []).join(", ")}</b><br />
                    Interval: <b>{Math.round((discStatus.interval_seconds || 0) / 3600)}h</b> · Enabled: <b style={{ color: discStatus.enabled ? "#34D399" : "#EF4444" }}>{discStatus.enabled ? "YES" : "NO"}</b>
                    {discStatus.last_run && <> · Last run: <b>{(discStatus.last_run.finished_at || "").slice(0, 19)}</b></>}
                  </div>
                </div>
                <button
                  data-testid="discovery-run-now"
                  disabled={discRunning}
                  onClick={async () => {
                    setDiscRunning(true);
                    try {
                      await api.post("/admin/discovery/run");
                      showToast("Sweep started — refresh in ~30 s");
                    } catch (e) { showToast(e.response?.data?.detail || "Failed"); }
                    finally { setDiscRunning(false); }
                  }}
                  style={btn()}>{discRunning ? "Starting…" : "Run sweep now"}</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, marginTop: 14 }}>
                {["pending", "approved", "rejected", "stale"].map((s) => (
                  <div key={s} data-testid={`discovery-stat-${s}`} style={{ padding: 10, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                    <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{s}</div>
                    <div style={{ color: C.text, fontFamily: "'Space Grotesk',sans-serif", fontSize: 20, fontWeight: 800, marginTop: 4 }}>
                      {(discStatus.candidate_counts?.[s] || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
                <div style={{ padding: 10, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Rejection memory</div>
                  <div style={{ color: C.muted, fontFamily: "'Space Grotesk',sans-serif", fontSize: 20, fontWeight: 800, marginTop: 4 }}>
                    {(discStatus.rejection_memory || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {/* Filter row + queue */}
            <div style={card}>
              <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <select
                  data-testid="discovery-filter-status"
                  value={discFilter.status}
                  onChange={(e) => setDiscFilter({ ...discFilter, status: e.target.value })}
                  style={{ padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="stale">Stale</option>
                </select>
                <select
                  data-testid="discovery-filter-country"
                  value={discFilter.country}
                  onChange={(e) => setDiscFilter({ ...discFilter, country: e.target.value })}
                  style={{ padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                  <option value="">All countries</option>
                  <option value="GH">Ghana</option>
                  <option value="NG">Nigeria</option>
                  <option value="SN">Senegal</option>
                  <option value="CI">Côte d&apos;Ivoire</option>
                </select>
                <select
                  data-testid="discovery-filter-source"
                  value={discFilter.source}
                  onChange={(e) => setDiscFilter({ ...discFilter, source: e.target.value })}
                  style={{ padding: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                  <option value="">All sources</option>
                  <option value="radio_browser">radio-browser</option>
                  <option value="xiph">xiph</option>
                  <option value="wikidata">wikidata</option>
                </select>
                <div style={{ marginLeft: "auto", color: C.muted, fontSize: 12, alignSelf: "center" }}>
                  {discCandidates.length} shown
                </div>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>Name</th>
                  <th style={th}>Country</th>
                  <th style={th}>Source</th>
                  <th style={th}>Probe</th>
                  <th style={th}>Stream</th>
                  <th style={th}>Discovered</th>
                  <th style={th}></th>
                </tr></thead>
                <tbody>
                  {discCandidates.map((c) => {
                    const probeStatus = c.probe?.reachable ? "LIVE" : (c.stream_url ? "UNREACHABLE" : "NO STREAM");
                    const probeColor = c.probe?.reachable ? "#10B981" : (!c.stream_url ? "#94A3B8" : "#F59E0B");
                    const sourceBg = c.source === "radio_browser" ? "#3B82F622" : c.source === "xiph" ? "#8B5CF622" : "#F59E0B22";
                    return (
                      <tr key={c.id} data-testid={`discovery-row-${c.id.slice(0,8)}`}>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: C.text }}>{c.name}</div>
                          {c.probe?.icy_name && <div style={{ color: C.muted, fontSize: 11 }}>icy: {c.probe.icy_name}</div>}
                        </td>
                        <td style={td}>{c.countrycode}</td>
                        <td style={td}>
                          <span style={{ padding: "2px 8px", background: sourceBg, color: C.text, borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                            {c.source}
                          </span>
                        </td>
                        <td style={td}>
                          <span style={{ color: probeColor, fontWeight: 700, fontSize: 11, letterSpacing: "0.05em" }}>
                            {probeStatus}
                          </span>
                        </td>
                        <td style={{ ...td, fontFamily: "monospace", fontSize: 11, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {c.stream_url ? (
                            <a href={c.stream_url} target="_blank" rel="noreferrer" style={{ color: C.cyan }}>
                              {c.stream_url.length > 40 ? c.stream_url.slice(0, 40) + "…" : c.stream_url}
                            </a>
                          ) : <span style={{ color: C.muted }}>—</span>}
                        </td>
                        <td style={{ ...td, fontSize: 11, color: C.muted }}>
                          {(c.discovered_at || "").slice(0, 16).replace("T", " ")}
                        </td>
                        {c.status === "pending" && (
                          <td style={{ ...td, display: "flex", gap: 6 }}>
                            <button
                              data-testid={`discovery-approve-${c.id.slice(0,8)}`}
                              disabled={discBusy || !c.stream_url}
                              onClick={async () => {
                                setDiscBusy(true);
                                try {
                                  await api.post(`/admin/discovery/candidates/${c.id}/approve`, {});
                                  setDiscCandidates((rows) => rows.filter((r) => r.id !== c.id));
                                  showToast(`Approved: ${c.name}`);
                                } catch (e) { showToast(e.response?.data?.detail || "Approve failed"); }
                                finally { setDiscBusy(false); }
                              }}
                              style={btn()}>Approve</button>
                            <button
                              data-testid={`discovery-reject-${c.id.slice(0,8)}`}
                              disabled={discBusy}
                              onClick={async () => {
                                const reason = window.prompt("Reject reason (duplicate / dead / not our market / junk):", "junk");
                                if (!reason) return;
                                setDiscBusy(true);
                                try {
                                  await api.post(`/admin/discovery/candidates/${c.id}/reject`, { reason });
                                  setDiscCandidates((rows) => rows.filter((r) => r.id !== c.id));
                                  showToast("Rejected");
                                } catch (e) { showToast(e.response?.data?.detail || "Reject failed"); }
                                finally { setDiscBusy(false); }
                              }}
                              style={btn("danger")}>Reject</button>
                          </td>
                        )}
                        {c.status !== "pending" && (
                          <td style={{ ...td, color: C.muted, fontSize: 11 }}>{c.status}</td>
                        )}
                      </tr>
                    );
                  })}
                  {discCandidates.length === 0 && (
                    <tr><td colSpan={7} style={{ ...td, color: C.muted, textAlign: "center", padding: 32 }}>
                      No candidates match the current filter.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* AUDIT TRAIL */}
        {tab === "blog" && (
          <div style={card} data-testid="admin-blog-tab">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>Blog editorial hold</div>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>
                  Auto-generated posts are saved as drafts. Nothing goes live until you approve it here.
                </div>
              </div>
              <button data-testid="admin-blog-generate" disabled={blogBusy} style={btn()}
                onClick={async () => {
                  setBlogBusy(true);
                  try {
                    const r = await api.post("/admin/blog/generate");
                    showToast(r.data.ok ? `Draft created: ${r.data.title}` : r.data.reason);
                    const d = await api.get("/admin/blog/drafts");
                    setBlogDrafts(d.data.drafts || []);
                  } catch (e) { showToast(e.response?.data?.detail || "Generation failed"); }
                  setBlogBusy(false);
                }}>
                {blogBusy ? "Generating…" : "Generate draft now"}
              </button>
            </div>
            {blogDrafts.length === 0 && (
              <div data-testid="admin-blog-empty" style={{ color: C.muted, fontSize: 13, padding: "18px 0" }}>
                No drafts awaiting review.
              </div>
            )}
            {blogDrafts.map((d) => (
              <div key={d.id} data-testid={`admin-blog-draft-${d.id}`}
                style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 320px" }}>
                    <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{d.title}</div>
                    <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>
                      /blog/{d.slug} · drafted {(d.created_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                    <div style={{ color: C.muted, fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>{d.excerpt}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <button data-testid={`admin-blog-approve-${d.id}`} style={btn()}
                      onClick={async () => {
                        try {
                          await api.post(`/admin/blog/${d.id}/approve`);
                          showToast("Published");
                          setBlogDrafts((prev) => prev.filter((x) => x.id !== d.id));
                        } catch (e) { showToast(e.response?.data?.detail || "Approve failed"); }
                      }}>Approve & publish</button>
                    <button data-testid={`admin-blog-reject-${d.id}`} style={btn("danger")}
                      onClick={async () => {
                        try {
                          await api.delete(`/admin/blog/${d.id}`);
                          showToast("Draft rejected");
                          setBlogDrafts((prev) => prev.filter((x) => x.id !== d.id));
                        } catch (e) { showToast(e.response?.data?.detail || "Reject failed"); }
                      }}>Reject</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "audit" && (
          <div data-testid="admin-audit-tab" style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ color: C.text, fontSize: 15, fontWeight: 700 }}>Tamper-evident audit trail</div>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
                  Each row is hash-chained to its predecessor. Any tampering breaks the chain — click verify to walk the entire ledger.
                </div>
              </div>
              <button data-testid="audit-verify-btn" onClick={verifyAuditChain} style={btn()}>
                {auditVerify?.loading ? "Verifying…" : "Verify chain"}
              </button>
            </div>
            {auditVerify && !auditVerify.loading && (
              <div data-testid="audit-verify-result" style={{
                background: auditVerify.ok ? T.greenAccent + "1a" : "#EF44441a",
                border: `1px solid ${auditVerify.ok ? T.greenAccent : "#EF4444"}55`,
                color: auditVerify.ok ? T.greenAccent : "#EF4444",
                padding: "10px 14px", borderRadius: 8, fontSize: 12, marginBottom: 14 }}>
                {auditVerify.ok
                  ? `✓ Chain intact — ${auditVerify.rows_checked} rows verified, head ${(auditVerify.head_hash || "").slice(0, 12)}…`
                  : `⚠ Chain BROKEN at row ${auditVerify.broken_at || "?"} (${(auditVerify.broken_at_ts || "").slice(0, 16)}). Expected hash ${(auditVerify.expected_this_hash || "").slice(0, 12)}… got ${(auditVerify.found_this_hash || "").slice(0, 12)}…`}
              </div>
            )}
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>{audit.length} events (most recent first)</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>When (UTC)</th>
                  <th style={th}>Actor</th>
                  <th style={th}>Action</th>
                  <th style={th}>Target</th>
                  <th style={th}>Details</th>
                  <th style={th}>Hash</th>
                </tr></thead>
                <tbody>{audit.map((row) => (
                  <tr key={row.id} data-testid={`audit-row-${row.id}`}>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 11, whiteSpace: "nowrap" }}>{(row.ts || "").slice(0, 19).replace("T", " ")}</td>
                    <td style={td}>{row.actor_email || "—"}<div style={{ color: C.muted, fontSize: 10 }}>{row.actor_role}</div></td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{row.action}</td>
                    <td style={td}>{row.target_type || "—"}<div style={{ color: C.muted, fontSize: 10, fontFamily: "'DM Mono',monospace" }}>{(row.target_id || "").slice(0, 14)}</div></td>
                    <td style={{ ...td, color: C.muted, fontSize: 11 }}>
                      {row.metadata && Object.keys(row.metadata).length > 0
                        ? Object.entries(row.metadata).slice(0, 3).map(([k, v]) => `${k}=${typeof v === "string" ? v.slice(0, 22) : v}`).join(" · ")
                        : "—"}
                    </td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 10, color: C.cyan }}>{(row.this_hash || "").slice(0, 12)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "streams" && (
          <div data-testid="admin-streams-tab" style={{ display: "grid", gap: 16 }}>
            {/* Hosting snapshot */}
            {streamsHosting && (
              <div style={{ ...card, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                {[
                  ["Concurrent cap", streamsHosting.cap],
                  ["Active", streamsHosting.active],
                  ["Waitlist length", streamsHosting.waitlisted],
                  ["Slots remaining", streamsHosting.slots_remaining],
                ].map(([k, v]) => (
                  <div key={k} data-testid={`admin-streams-kpi-${k.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div style={{ color: C.muted, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>{k}</div>
                    <div style={{ color: C.text, fontSize: 22, fontWeight: 800, fontFamily: "'DM Mono',monospace" }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
            {/* A/B Analytics — CTA click funnel (last 30 days) */}
            <div style={card} data-testid="admin-streams-clicks">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                <div style={{ color: C.text, fontSize: 15, fontWeight: 700 }}>
                  A/B click funnel <span style={{ color: C.muted, fontSize: 11, fontWeight: 500 }}>· last 30 days · unique per (IP, day)</span>
                </div>
              </div>
              {streamsClicks.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 13 }}>No clicks yet.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={th}>CTA</th>
                      <th style={{ ...th, textAlign: "right" }}>Unique visitors</th>
                      <th style={{ ...th, textAlign: "right" }}>Total clicks</th>
                    </tr></thead>
                    <tbody>{streamsClicks.map((r) => (
                      <tr key={r.cta_id} data-testid={`admin-streams-click-${r.cta_id}`}>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{r.cta_id}</td>
                        <td style={{ ...td, textAlign: "right", fontFamily: "'DM Mono',monospace" }}>{r.unique_visitors.toLocaleString()}</td>
                        <td style={{ ...td, textAlign: "right", fontFamily: "'DM Mono',monospace" }}>{r.total_clicks.toLocaleString()}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Review queue */}
            <div style={card}>
              <div style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 10 }}>
                Application queue ({streamsQueue.length})
              </div>
              {streamsQueue.length === 0 && (
                <div style={{ color: C.muted, fontSize: 13 }}>Nothing awaiting review.</div>
              )}
              {streamsQueue.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={th}>Submitted</th>
                      <th style={th}>Station</th>
                      <th style={th}>NCA #</th>
                      <th style={th}>Contact</th>
                      <th style={th}>Status</th>
                      <th style={th}>Actions</th>
                    </tr></thead>
                    <tbody>{streamsQueue.map((a) => (
                      <tr key={a.id} data-testid={`admin-streams-row-${a.id}`}>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: 11, whiteSpace: "nowrap" }}>
                          {(a.submitted_at || "").slice(0, 16).replace("T", " ")}
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 700 }}>{a.station_name}</div>
                          <div style={{ color: C.muted, fontSize: 11 }}>slug: {a.requested_slug}</div>
                        </td>
                        <td style={{ ...td, fontFamily: "'DM Mono',monospace" }}>{a.nca_licence_number}</td>
                        <td style={td}>{a.contact_email}<div style={{ color: C.muted, fontSize: 11 }}>{a.contact_phone || ""}</div></td>
                        <td style={td}>{a.status}</td>
                        <td style={td}>
                          <button data-testid={`admin-streams-approve-${a.id}`}
                            style={{ ...btn(), marginRight: 6 }}
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/admin/streams/${a.id}/decide`, { decision: "approve", note: "" });
                                showToast(`Approved: ${data.outcome}`);
                                const q = await api.get("/admin/streams/queue");
                                setStreamsQueue(q.data.queue || []);
                                setStreamsHosting(q.data.hosting || null);
                              } catch (e) { showToast(e.response?.data?.detail || "Approve failed"); }
                            }}>Approve</button>
                          <button data-testid={`admin-streams-reject-${a.id}`}
                            style={btn("danger")}
                            onClick={async () => {
                              const note = window.prompt("Reason for rejection (optional):") || "";
                              try {
                                await api.post(`/admin/streams/${a.id}/decide`, { decision: "reject", note });
                                showToast("Rejected");
                                setStreamsQueue((prev) => prev.filter((x) => x.id !== a.id));
                              } catch (e) { showToast(e.response?.data?.detail || "Reject failed"); }
                            }}>Reject</button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
            {/* Live configs */}
            <div style={card}>
              <div style={{ color: C.text, fontSize: 15, fontWeight: 700, marginBottom: 10 }}>
                Active hosted streams ({streamsConfigs.length})
              </div>
              {streamsConfigs.length === 0 && (
                <div style={{ color: C.muted, fontSize: 13 }}>No streams active yet.</div>
              )}
              {streamsConfigs.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={th}>Slug</th>
                      <th style={th}>Station</th>
                      <th style={th}>Status</th>
                      <th style={th}>Listeners</th>
                      <th style={th}>Bandwidth (GB)</th>
                      <th style={th}>Actions</th>
                    </tr></thead>
                    <tbody>{streamsConfigs.map((c) => {
                      const gbUsed = (c.bandwidth_bytes_this_period || 0) / 1024 ** 3;
                      return (
                        <tr key={c.id} data-testid={`admin-streams-config-${c.id}`}>
                          <td style={{ ...td, fontFamily: "'DM Mono',monospace" }}>{c.mount_slug}</td>
                          <td style={td}>{c.station_name}</td>
                          <td style={td}>{c.status}</td>
                          <td style={{ ...td, fontFamily: "'DM Mono',monospace" }}>{c.last_telemetry_listeners || 0}</td>
                          <td style={{ ...td, fontFamily: "'DM Mono',monospace" }}>{gbUsed.toFixed(2)} / {c.bandwidth_gb_cap}</td>
                          <td style={td}>
                            <button data-testid={`admin-streams-suspend-${c.id}`}
                              style={btn("danger")}
                              onClick={async () => {
                                const reason = window.prompt("Reason for suspension:") || "";
                                if (!reason) return;
                                try {
                                  const { data } = await api.post(`/admin/streams/configs/${c.id}/suspend`, { reason });
                                  showToast(data.promoted ? `Suspended. Waitlist promoted: ${data.promoted.slice(0, 8)}` : "Suspended");
                                  const [q, cfgs] = await Promise.all([
                                    api.get("/admin/streams/queue"),
                                    api.get("/admin/streams/configs"),
                                  ]);
                                  setStreamsQueue(q.data.queue || []);
                                  setStreamsHosting(q.data.hosting || null);
                                  setStreamsConfigs(cfgs.data.configs || []);
                                } catch (e) { showToast(e.response?.data?.detail || "Suspend failed"); }
                              }}>Suspend</button>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal: Add NCA entry */}
        {ncaModal && (
          <Modal title="Add NCA registry entry" onClose={() => setNcaModal(null)} testid="modal-nca-add">
            <ModalInput label="Licence number" placeholder="e.g. FM/ACC/2026/0100"
              value={ncaModal.licence_number} onChange={(v) => setNcaModal({ ...ncaModal, licence_number: v })} testid="modal-nca-licence" />
            <ModalInput label="Station name" placeholder="Joy FM 99.7"
              value={ncaModal.station_name} onChange={(v) => setNcaModal({ ...ncaModal, station_name: v })} testid="modal-nca-station" />
            <ModalInput label="Licensee" placeholder="Multimedia Broadcasting Ltd"
              value={ncaModal.licensee} onChange={(v) => setNcaModal({ ...ncaModal, licensee: v })} testid="modal-nca-licensee" />
            <ModalInput label="Frequency (optional)" placeholder="99.7"
              value={ncaModal.frequency} onChange={(v) => setNcaModal({ ...ncaModal, frequency: v })} testid="modal-nca-freq" />
            <ModalInput label="Region (optional, e.g. GH-AA)" placeholder="GH-AA"
              value={ncaModal.region} onChange={(v) => setNcaModal({ ...ncaModal, region: v })} testid="modal-nca-region" />
            <ModalActions>
              <button data-testid="modal-nca-cancel" onClick={() => setNcaModal(null)} style={btn("ghost")}>Cancel</button>
              <button data-testid="modal-nca-save" onClick={submitAddNca} style={btn()}>Add entry</button>
            </ModalActions>
          </Modal>
        )}

        {/* Modal: Bulk NCA import */}
        {bulkNcaModal && (
          <Modal title="Bulk import NCA entries" onClose={() => setBulkNcaModal(false)} testid="modal-nca-bulk">
            <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.6, marginBottom: 10 }}>
              Paste one entry per line in CSV format:{" "}
              <code style={{ background: C.bg, padding: "2px 6px", borderRadius: 4, color: C.cyan, fontSize: 11 }}>
                licence_number,station_name,licensee,frequency,region
              </code>
              <br />First line may be a header. Duplicate licence numbers are skipped automatically.
            </div>
            <textarea data-testid="modal-bulk-csv" value={bulkCsv} onChange={(e) => setBulkCsv(e.target.value)}
              placeholder={"FM/ACC/2026/0200,New FM 88.5,New Broadcasting Co.,88.5,GH-AA\nFM/KUM/2026/0201,Test FM 90.1,Test Ltd,90.1,GH-AH"}
              style={{ width: "100%", minHeight: 200, padding: 12, background: C.bg, border: `1px solid ${C.border}`,
                color: C.text, borderRadius: 8, fontFamily: "'DM Mono',monospace", fontSize: 12, marginBottom: 12 }} />
            {bulkResult && (
              <div data-testid="modal-bulk-result" style={{ background: T.greenAccent + "22", color: T.greenAccent,
                border: `1px solid ${T.greenAccent}55`, padding: "10px 14px", borderRadius: 8, fontSize: 12, marginBottom: 12 }}>
                ✓ {bulkResult.message}
              </div>
            )}
            <ModalActions>
              <button data-testid="modal-bulk-close" onClick={() => setBulkNcaModal(false)} style={btn("ghost")}>Close</button>
              <button data-testid="modal-bulk-import" onClick={submitBulkNca} style={btn()}>Import</button>
            </ModalActions>
          </Modal>
        )}

        {/* Modal: Set plan */}
        {planModal && (
          <Modal title={`Set plan for ${planModal.user.email}`} onClose={() => setPlanModal(null)} testid="modal-plan">
            <div style={{ color: C.muted, fontSize: 12, marginBottom: 12 }}>
              Current plan: <b style={{ color: C.cyan }}>{planModal.user.plan || "free"}</b>
            </div>
            <select data-testid="modal-plan-select" value={planModal.plan} onChange={(e) => setPlanModal({ ...planModal, plan: e.target.value })}
              style={{ width: "100%", padding: "11px 14px", background: C.bg, border: `1px solid ${C.border}`,
                color: C.text, borderRadius: 8, fontFamily: "inherit", fontSize: 13, marginBottom: 16 }}>
              {["free", "starter", "pro", "agency", "station"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <ModalActions>
              <button data-testid="modal-plan-cancel" onClick={() => setPlanModal(null)} style={btn("ghost")}>Cancel</button>
              <button data-testid="modal-plan-save" onClick={confirmSetPlan} style={btn()}>Save plan</button>
            </ModalActions>
          </Modal>
        )}

        {/* Modal: Reject claim */}
        {rejectModal && (
          <Modal title="Reject station claim" onClose={() => setRejectModal(null)} testid="modal-reject">
            <ModalInput label="Reason (shown to the requester)" value={rejectModal.reason}
              onChange={(v) => setRejectModal({ ...rejectModal, reason: v })} testid="modal-reject-reason" />
            <ModalActions>
              <button data-testid="modal-reject-cancel" onClick={() => setRejectModal(null)} style={btn("ghost")}>Cancel</button>
              <button data-testid="modal-reject-confirm" onClick={confirmRejectClaim} style={btn("danger")}>Reject claim</button>
            </ModalActions>
          </Modal>
        )}

        {/* Modal: Generic confirm (replaces window.confirm) */}
        {confirmModal && (
          <Modal title={confirmModal.title} onClose={() => setConfirmModal(null)} testid="modal-confirm">
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.55, margin: "0 0 20px" }}>{confirmModal.body}</p>
            <ModalActions>
              <button data-testid="modal-confirm-cancel" onClick={() => setConfirmModal(null)} style={btn("ghost")}>Cancel</button>
              <button data-testid="modal-confirm-ok" onClick={confirmModal.onConfirm} style={btn(confirmModal.variant || "primary")}>
                {confirmModal.ctaLabel || "Confirm"}
              </button>
            </ModalActions>
          </Modal>
        )}
      </div>
    </div>
  );
}

// ─────── Modal primitives ───────
function Modal({ title, onClose, testid, children }) {
  return (
    <div data-testid={testid} onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "#000a", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
          padding: 24, width: "100%", maxWidth: 540, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h3 style={{ color: C.text, fontSize: 17, fontWeight: 700, margin: 0 }}>{title}</h3>
          <button data-testid={`${testid}-x`} onClick={onClose}
            style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
function ModalInput({ label: lbl, value, onChange, placeholder, testid }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
      <input data-testid={testid} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", padding: "11px 14px", background: C.bg, border: `1px solid ${C.border}`,
          color: C.text, borderRadius: 8, fontFamily: "inherit", fontSize: 13, outline: "none" }} />
    </div>
  );
}
function ModalActions({ children }) {
  return <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>{children}</div>;
}
